import { useState } from "react";
import { CatalogPicker } from "@core/components/CatalogPicker";
import { BudgetBar, List, ListRow } from "@core/components/List";
import { Sheet } from "@core/components/Sheet";
import { Value } from "@core/components/Value";
import { AmountSheet, MasteryOfferPicker, NameSheet, SpellSchoolSheet, instanceNaming } from "./parts";
import { alreadyTaken, useSm, type EntryDetails } from "./view";

type PurchaseKind = "skill" | "attribute" | "mastery" | "spell" | "strength" | "resource" | "language";

/** Costs and limits of start experience from the rule data (`startXpCosts`, GRW p. 25). */
interface Costs {
  attribute: number;
  skillPoint: number;
  skillMax: number;
  strengthLevel: number;
  spellByGrade: number[];
  spellMinPoints: number[];
  mastery: number;
  masteryMinPoints: number;
  resourcePoint: number;
  resourceMax: number;
  language: number;
}

/** Step 9: spend the start experience on the purchases of GRW p. 25, or keep it and close the step. */
export function ExperienceStep() {
  const { view, apply } = useSm();
  return (
    <div className="stack">
      <StartXpPurchases />
      <section className="card stack" aria-label="Abschluss der Start-Erfahrung">
        {view.startXpConfirmed ? (
          <div className="row spread">
            <span>✓ Start-Erfahrung abgeschlossen</span>
            <button type="button" className="link" onClick={() => void apply({ type: "sm.confirmStartXp", payload: { confirmed: false } })}>
              Wieder öffnen
            </button>
          </div>
        ) : (
          <>
            <p className="muted small">
              Du darfst die Start-Erfahrung ganz oder teilweise aufbewahren. Bist du mit den Käufen fertig, schließe den Schritt ab.
            </p>
            <div className="actions">
              <button type="button" className="button primary" onClick={() => void apply({ type: "sm.confirmStartXp", payload: { confirmed: true } })}>
                Start-Erfahrung abschließen
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}

/**
 * The purchases with start experience. In career mode (`career`) they spend the start experience that taking back a purchase of creation
 * opened again; a purchase made is taken back in the log there.
 */
export function StartXpPurchases(props: { career?: boolean }) {
  const { view, apply, confirm, budget } = useSm();
  const costs = view.costs as unknown as Costs;
  const limits = view.startXpLimits ?? { spellGrade: 0, masteryLevel: 0 };
  const grades = costs.spellByGrade.map((_, grade) => grade).join("/");
  const xp = view.allocationBudgets.startXp?.xp;
  const left = props.career ? view.budgets.startXpReopened : view.budgets.startXp;
  const [kind, setKind] = useState<PurchaseKind | null>(null);
  const [picked, setPicked] = useState<{ id: string; label: string; details: EntryDetails & { skill?: string; schools?: { school: string; grade: number }[] } } | null>(null);
  const [instance, setInstance] = useState<string | undefined>(undefined);
  const [askingName, setAskingName] = useState(false);

  const kinds: { kind: PurchaseKind; label: string; cost: string; hint: string; catalog: string | null }[] = [
    { kind: "skill", label: "Fertigkeitspunkte", cost: `${costs.skillPoint} EP je Punkt`, hint: `höchstens ${costs.skillMax} Punkte je Fertigkeit`, catalog: "skills" },
    { kind: "attribute", label: "Attribut +1", cost: `${costs.attribute} EP`, hint: "höchstens Startwert +1", catalog: null },
    { kind: "mastery", label: "Meisterschaft", cost: `${costs.mastery} EP`, hint: `Schwelle ${limits.masteryLevel}, Fertigkeit mit ${costs.masteryMinPoints} Punkten`, catalog: null },
    { kind: "spell", label: "Zauber", cost: `${costs.spellByGrade.join("/")} EP`, hint: `nach Grad ${grades}, Schule mit ${costs.spellMinPoints.join("/")} Punkten`, catalog: "spells" },
    { kind: "strength", label: "Stärke", cost: `${costs.strengthLevel} EP je Stufe`, hint: "auch Stärken mit *", catalog: "strengths" },
    { kind: "resource", label: "Ressource", cost: `${costs.resourcePoint} EP je Punkt`, hint: `höchstens ${costs.resourceMax}`, catalog: "resources" },
    { kind: "language", label: "Sprache", cost: `${costs.language} EP`, hint: "eine weitere Sprache", catalog: "languages" },
  ];
  /** Start experience buys spells only up to the grade of its table; masteries come from the engine's offers per skill. */
  const catalogFilter: Record<string, number> | undefined = kind === "spell" ? { grade: limits.spellGrade } : undefined;
  const definition = kinds.find((k) => k.kind === kind);

  const buy = (payload: Record<string, unknown>, title: string) => {
    setKind(null);
    setPicked(null);
    setInstance(undefined);
    confirm({ type: "sm.addPurchase", payload }, title);
  };

  return (
    <div className="stack">
      <section className="card stack">
        {props.career ? (
          <p>
            Wieder offen: <strong>{left && <Value valueKey={left} />} EP</strong>
            <span className="muted small"> · Was du hier nicht ausgibst, bleibt freie Erfahrung zu den Kosten des Karrieremodus.</span>
          </p>
        ) : (
          <BudgetBar
            label="Start-Erfahrung"
            used={budget(xp?.spent ?? "")}
            total={budget(xp?.total ?? "")}
            usedText={<>{xp && <Value valueKey={xp.spent} />} EP</>}
            totalText={<>{xp && <Value valueKey={xp.total} />} EP</>}
            hint="Was übrig bleibt, wird beim Wechsel in den Karrieremodus als freie Erfahrung gutgeschrieben." />
        )}
        <List aria-label="Käufe">
          {view.startXp.length === 0 && <li className="list-empty">Noch nichts gekauft.</li>}
          {view.startXp.map((p) => (
            <ListRow
              key={p.index}
              title={p.label}
              value={`${p.cost} EP`}
              actions={
                props.career ? undefined : (
                  <button type="button" className="link danger" onClick={() => void apply({ type: "sm.removePurchase", payload: { index: p.index } })}>
                    Zurücknehmen
                  </button>
                )
              }
            />
          ))}
        </List>
        {props.career && <p className="muted small">Einen Kauf nimmst du im Protokoll zurück.</p>}
      </section>

      <section className="card stack">
        <h3>Einsetzen für …</h3>
        <div className="quick-actions">
          {kinds.map((k) => (
            <button key={k.kind} type="button" className="quick-action purchase" onClick={() => setKind(k.kind)}>
              <span>{k.label}</span>
              <span className="purchase-cost">{k.cost}</span>
              <span className="muted small">{k.hint}</span>
            </button>
          ))}
        </div>
        <p className="muted small">
          Kosten zu Spielbeginn nach GRW S. 25. Übrig: {left && <Value valueKey={left} />} EP.
        </p>
      </section>

      {kind === "attribute" && (
        <Sheet title="Attribut steigern" onClose={() => setKind(null)}>
          <p className="muted small">{costs.attribute} EP je Punkt; mit Start-Erfahrung höchstens auf den Startwert +1.</p>
          <List>
            {view.attributes.map((a) => (
              <ListRow
                key={a.id}
                title={a.name}
                value={<Value valueKey={a.value} />}
                actions={
                  <button type="button" className="button secondary" onClick={() => buy({ kind: "attribute", target: a.id, amount: 1 }, `${a.name} +1`)}>
                    +1
                  </button>
                }
              />
            ))}
          </List>
        </Sheet>
      )}

      {definition?.catalog && !picked && (
        <CatalogPicker
          type={definition.catalog}
          title={definition.label}
          hint={`${definition.cost}; ${definition.hint}.`}
          filter={catalogFilter}
          unavailable={alreadyTaken(view, definition.catalog)}
          onClose={() => setKind(null)}
          onPick={(item) => {
            const details = (item.details ?? {}) as EntryDetails & { skill?: string; schools?: { school: string; grade: number }[] };
            const chosen = { id: item.id, label: item.label.de, details };
            switch (kind) {
              case "skill":
                setPicked(chosen);
                break;
              case "resource":
                setPicked(chosen);
                if (details.multipleInstances || details.parameter) setAskingName(true);
                break;
              case "spell": {
                const schools = (details.schools ?? []).filter((s) => s.grade <= limits.spellGrade);
                if (schools.length > 1) setPicked({ ...chosen, details: { ...details, schools } });
                else buy({ kind, target: item.id, amount: 1, school: schools[0]?.school }, `Zauber ${chosen.label}`);
                break;
              }
              default:
                buy({ kind, target: item.id, amount: 1 }, chosen.label);
            }
          }}
        />
      )}

      {kind === "mastery" && (
        <MasteryOfferPicker
          title="Meisterschaft"
          offers={view.startXpMasteryOffers}
          intro={`Für welche Fertigkeit? Zur Wahl stehen die Fertigkeiten mit mindestens ${costs.masteryMinPoints} Punkten (GRW S. 25).`}
          empty={`Noch keine Fertigkeit hat ${costs.masteryMinPoints} Punkte.`}
          hint={`${definition?.cost}; ${definition?.hint}.`}
          onClose={() => setKind(null)}
          onPick={(mastery, skill) => buy({ kind, target: mastery.id, amount: 1, skill: skill.id }, `Meisterschaft ${mastery.name} in ${skill.name}`)}
        />
      )}

      {picked && kind === "spell" && (
        <SpellSchoolSheet
          spell={picked.label}
          schools={picked.details.schools ?? []}
          onClose={() => {
            setPicked(null);
            setKind(null);
          }}
          onPick={(option) => buy({ kind, target: picked.id, amount: 1, school: option.school }, `Zauber ${picked.label}`)}
        />
      )}

      {picked && kind === "resource" && askingName && (
        <NameSheet
          {...instanceNaming(picked.label, picked.details.parameter?.label)}
          onClose={() => {
            setPicked(null);
            setKind(null);
          }}
          onSubmit={(name) => {
            setInstance(name);
            setAskingName(false);
          }}
        />
      )}

      {picked && (kind === "skill" || (kind === "resource" && !askingName)) && (
        <AmountSheet
          title={picked.label}
          hint={kind === "skill" ? `${costs.skillPoint} EP je Punkt, höchstens ${costs.skillMax} Punkte je Fertigkeit.` : `${costs.resourcePoint} EP je Punkt, höchstens ${costs.resourceMax}.`}
          min={1}
          max={kind === "skill" ? costs.skillMax : costs.resourceMax}
          onClose={() => {
            setPicked(null);
            setKind(null);
          }}
          onSubmit={(amount) => buy({ kind, target: picked.id, amount, param: instance }, `${picked.label} +${amount}`)}
        />
      )}
    </div>
  );
}
